import {
  collection,
  addDoc,
  doc,
  updateDoc,
  serverTimestamp,
  query,
  where,
  orderBy,
  limit,
  getDocs,
} from "firebase/firestore";
import { firebase, analytics } from "./firebase-config";
import { logEvent, setUserId } from "firebase/analytics";
import { COLLECTIONS } from "./config";

/** Human-readable labels for userActionLog.event_name (snake_case keys) */
export const USER_ACTION_EVENT_LABELS = {
  register: "Registered",
  login: "Login",
  create_employee: "Employee created",
  create_client: "Client created",
  payment: "Payment",
  create_issue: "Issue created",
  create_supply: "Supply request",
  create_support: "Support ticket",
  register_step1: "Registration Step 1 Completed",
  register_step2: "Registration Step 2 Completed",
  verify_email_success: "Email Verification Success",
  verify_email_failed: "Email Verification Failed",
  landed_on_verify_email_sent: "Navigated to Verification Sent Page",
  verify_email: "Email Verification Started",
};

/** Event names stored in userActionLog.event_name */
export const USER_ACTION_EVENTS = {
  REGISTER: "register",
  LOGIN: "login",
  CREATE_EMPLOYEE: "create_employee",
  CREATE_CLIENT: "create_client",
  PAYMENT: "payment",
  CREATE_ISSUE: "create_issue",
  CREATE_SUPPLY: "create_supply",
  CREATE_SUPPORT: "create_support",
  REGISTER_STEP1: "register_step1",
  REGISTER_STEP2: "register_step2",
  VERIFY_EMAIL_SUCCESS: "verify_email_success",
  VERIFY_EMAIL_FAILED: "verify_email_failed",
  LANDED_ON_VERIFY_EMAIL_SENT: "landed_on_verify_email_sent",
  VERIFY_EMAIL: "verify_email",
};

/**
 * Persists an audit row to userActionLog and updates last activity on the target profile doc.
 * Call with void / fire-and-forget from UI; errors are logged only.
 *
 * @param {object} params
 * @param {string} params.userId - Acting user (auth uid)
 * @param {string} params.eventName - One of USER_ACTION_EVENTS values
 * @param {string} [params.email] - Acting user's email
 * @param {string} [params.lastActivityProfileId] - users/{id} to set lastUserActionAt (defaults to userId)
 */
export async function logUserAction({
  userId,
  eventName,
  email = "",
  displayName = "",
  lastActivityProfileId,
} = {}) {
  if (!userId || !eventName) return;

  const safeEmail = typeof email === "string" ? email.trim() : "";
  const safeName = typeof displayName === "string" ? displayName.trim() : "";
  const profileId = lastActivityProfileId || userId;

  try {
    await addDoc(collection(firebase, COLLECTIONS.USER_ACTION_LOG), {
      userId,
      event_name: eventName,
      email: safeEmail,
      displayName: safeName,
      createdAt: serverTimestamp(),
    });
  } catch (err) {
    console.error("[logUserAction] log write failed:", err?.message || err);
  }

  const isPlaceholder =
    profileId === "pre-register-step1" ||
    profileId === "pre-register-step2" ||
    profileId === "verified-user" ||
    profileId === "failed-verify-user" ||
    profileId === "landed-on-verify-sent" ||
    profileId === "verify-page-landed";

  try {
    if (!isPlaceholder) {
      setUserId(analytics, userId);
    }
    logEvent(analytics, eventName, {
      user_id: userId,
      user_email: safeEmail,
      user_name: safeName,
      method: "email", // context for register/login events
    });
  } catch (err) {
    console.warn("[logUserAction] analytics log failed:", err?.message || err);
  }

  if (!isPlaceholder) {
    try {
      await updateDoc(doc(firebase, COLLECTIONS.USERS, profileId), {
        lastUserActionAt: serverTimestamp(),
        lastUserActionEvent: eventName,
      });
    } catch (err) {
      console.error("[logUserAction] profile update failed:", err?.message || err);
    }
  }
}

export function logUserActionBackground(payload) {
  void logUserAction(payload);
}

/**
 * Latest log entry per user id (requires composite index: userId ASC, createdAt DESC).
 * @param {string[]} userIds
 * @returns {Promise<Record<string, { id: string; event_name: string; email: string; createdAt: unknown }>>}
 */
export async function fetchLatestUserActionLogsByUserIds(userIds = []) {
  const uniqueIds = [...new Set((userIds || []).filter(Boolean))];
  const result = {};
  const PARALLEL = 24;

  for (let i = 0; i < uniqueIds.length; i += PARALLEL) {
    const slice = uniqueIds.slice(i, i + PARALLEL);
    await Promise.all(
      slice.map(async (uid) => {
        try {
          const q = query(
            collection(firebase, COLLECTIONS.USER_ACTION_LOG),
            where("userId", "==", uid),
            orderBy("createdAt", "desc"),
            limit(1)
          );
          const snap = await getDocs(q);
          if (snap.empty) return;
          const docSnap = snap.docs[0];
          const data = docSnap.data();
          result[uid] = {
            id: docSnap.id,
            event_name: data.event_name,
            email: data.email,
            createdAt: data.createdAt,
          };
        } catch (err) {
          console.error("[fetchLatestUserActionLogsByUserIds]", uid, err?.message || err);
        }
      })
    );
  }
  return result;
}
